export interface ParsedItem {
  text: string;
  url: string | null;
}

const BULLET_PREFIX = /^(?:[*\-\u2022]\s+|\d+[.)]\s+)/;
const URL_REGEX = /https?:\/\/[^\s)>\]]+/;
const MARKDOWN_LINK = /\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/;

/**
 * Parse raw textarea input into an array of task items.
 * Handles bullet lists, numbered lists, URLs, and continuation lines.
 */
export function parseMultiLineInput(raw: string): ParsedItem[] {
  const lines = raw.split("\n");
  const items: ParsedItem[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) continue;

    const isBullet = BULLET_PREFIX.test(trimmed);
    // Indented non-bullet line is a continuation of the previous item
    const isIndented = /^\s+/.test(line) && !isBullet;

    if (isIndented && items.length > 0) {
      // Merge continuation with previous item
      const prev = items[items.length - 1];
      const { text: contText, url: contUrl } = extractUrlFromLine(trimmed);
      prev.text = prev.text + " \u2014 " + contText;
      if (!prev.url && contUrl) prev.url = contUrl;
      continue;
    }

    // Strip bullet prefix
    const stripped = trimmed.replace(BULLET_PREFIX, "").trim();
    if (!stripped) continue;

    const { text, url } = extractUrlFromLine(stripped);
    items.push({ text, url });
  }

  return items;
}

function extractUrlFromLine(line: string): ParsedItem {
  // Check for markdown links first: [text](url)
  const mdMatch = line.match(MARKDOWN_LINK);
  if (mdMatch) {
    const url = mdMatch[2];
    const withoutLink = line.replace(MARKDOWN_LINK, mdMatch[1]).trim();
    const text = withoutLink || url;
    return { text, url };
  }

  // Check for bare URLs
  const urlMatch = line.match(URL_REGEX);
  if (urlMatch) {
    const url = urlMatch[0];
    const withoutUrl = line.replace(url, "").trim();
    // If the line was just a URL, use the URL as the text too
    const text = withoutUrl || url;
    return { text, url };
  }

  return { text: line, url: null };
}
